import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, AlertCircle, RefreshCw, User, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientesSinCita() {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClientesSinCita();
  }, []);

  const loadClientesSinCita = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/clientes-sin-cita`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setClientes(data.expedientes);
      } else {
        toast.error('Error al cargar clientes');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const formatFecha = (fecha) => {
    try {
      return new Date(fecha).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return fecha;
    }
  };

  const getDiasBadgeColor = (dias) => {
    if (dias < 3) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (dias < 7) return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    return 'bg-red-500/20 text-red-400 border-red-500/30';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Header */}
      <header className="bg-gray-900/80 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <img src="/kompa-logo.png" alt="Kompa" className="h-10 w-auto" />
          </div>
          <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
            <Clock className="w-4 h-4 mr-2" />
            Citas Pendientes
          </Badge>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Clientes Sin Cita Agendada</h1>
            <p className="text-gray-400">Clientes que completaron Formulario 1 pero no han agendado su cita</p>
          </div>
          <Button
            onClick={loadClientesSinCita}
            className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Total Pendientes</p>
                  <p className="text-3xl font-bold text-white">{clientes.length}</p>
                </div>
                <AlertCircle className="w-12 h-12 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Urgentes (&gt;7 días)</p>
                  <p className="text-3xl font-bold text-red-400">
                    {clientes.filter(c => c.dias_pendiente >= 7).length}
                  </p>
                </div>
                <Clock className="w-12 h-12 text-red-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Recientes (&lt;3 días)</p>
                  <p className="text-3xl font-bold text-green-400">
                    {clientes.filter(c => c.dias_pendiente < 3).length}
                  </p>
                </div>
                <Calendar className="w-12 h-12 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Lista de Clientes */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Cargando clientes...</p>
          </div>
        ) : clientes.length === 0 ? (
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="text-center py-12">
              <p className="text-gray-400">¡Excelente! No hay clientes pendientes de agendar cita</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {clientes.map((cliente) => (
              <Card key={cliente.id} className="bg-gray-800/50 border-gray-700 hover:border-gray-600 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <User className="w-5 h-5 text-yellow-500" />
                        <p className="font-semibold text-white text-lg">
                          {cliente.solicitante.nombre_completo}
                        </p>
                        <Badge className={getDiasBadgeColor(cliente.dias_pendiente)}>
                          {cliente.dias_pendiente} {cliente.dias_pendiente === 1 ? 'día' : 'días'}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-400">
                        <div>
                          <span className="font-semibold text-gray-300">Expediente:</span>{' '}
                          {cliente.numero_expediente}
                        </div>
                        <div>
                          <span className="font-semibold text-gray-300">Cédula:</span>{' '}
                          {cliente.solicitante.cedula}
                        </div>
                        <div>
                          <span className="font-semibold text-gray-300">Email:</span>{' '}
                          {cliente.solicitante.email}
                        </div>
                        <div>
                          <span className="font-semibold text-gray-300">Teléfono:</span>{' '}
                          {cliente.solicitante.telefono || 'N/A'}
                        </div>
                        <div>
                          <span className="font-semibold text-gray-300">Creado:</span>{' '}
                          {formatFecha(cliente.created_at)}
                        </div>
                        <div>
                          <span className="font-semibold text-gray-300">Estado:</span>{' '}
                          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                            Cita Pendiente
                          </Badge>
                        </div>
                      </div>
                    </div>
                    
                    <Button
                      size="sm"
                      onClick={() => navigate(`/expedientes/${cliente.id}`)}
                      className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/30"
                    >
                      Ver Expediente
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
